import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Download, RefreshCw, Loader, CheckCircle,
  AlertCircle, BarChart3, Eye
} from 'lucide-react';
import {
  getCompletedSessions,
  generateReport,
  listReports,
  downloadReportPdf,
} from '../services/api';

function Reports() {
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState('generate');
  const [completedSessions, setCompletedSessions] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (activeSubTab === 'generate') {
      fetchCompletedSessions();
    } else {
      fetchReports();
    }
  }, [activeSubTab]);

  const fetchCompletedSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCompletedSessions();
      setCompletedSessions(data);
    } catch (err) {
      setError('Failed to fetch completed sessions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listReports();
      setReports(data);
    } catch (err) {
      setError('Failed to fetch reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (sessionId) => {
    setGenerating(sessionId);
    setError('');
    setSuccess('');
    try {
      await generateReport(sessionId);
      setSuccess('Report generated/regenerated successfully!');
      // Refresh both tabs
      fetchCompletedSessions();
      if (activeSubTab === 'history') {
        fetchReports();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
      console.error(err);
    } finally {
      setGenerating(null);
    }
  };

  const handleDownloadPdf = async (sessionId) => {
    try {
      await downloadReportPdf(sessionId);
    } catch (err) {
      setError('Failed to download PDF');
      console.error(err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 65) return 'text-brand-700 bg-brand-500/15';
    if (score >= 45) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const getRecColor = (rec) => {
    if (!rec) return 'text-ink-300 bg-ink-800';
    const r = rec.toLowerCase();
    if (r.includes('strong hire')) return 'text-green-700 bg-green-100';
    if (r.includes('hire')) return 'text-brand-700 bg-brand-500/15';
    if (r.includes('review')) return 'text-amber-700 bg-amber-100';
    return 'text-red-700 bg-red-100';
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveSubTab('generate')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSubTab === 'generate'
              ? 'bg-purple-600 text-white'
              : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Generate Reports
          </span>
        </button>
        <button
          onClick={() => setActiveSubTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSubTab === 'history'
              ? 'bg-purple-600 text-white'
              : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Generated Reports
          </span>
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {success}
        </div>
      )}

      {/* Generate Tab */}
      {activeSubTab === 'generate' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-ink-100">Completed Interview Sessions</h3>
            <button
              onClick={fetchCompletedSessions}
              className="flex items-center gap-2 px-3 py-2 text-sm text-ink-300 border border-ink-700 rounded-lg hover:bg-ink-800"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : completedSessions.length === 0 ? (
            <div className="text-center py-12 text-ink-400">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No completed interview sessions found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ink-800 border-b border-ink-700">
                    <th className="px-4 py-3 text-left font-medium text-ink-300">Candidate</th>
                    <th className="px-4 py-3 text-left font-medium text-ink-300">Position</th>
                    <th className="px-4 py-3 text-left font-medium text-ink-300">Date</th>
                    <th className="px-4 py-3 text-center font-medium text-ink-300">Report</th>
                    <th className="px-4 py-3 text-center font-medium text-ink-300">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {completedSessions.map((session) => (
                    <tr key={session.session_id} className="border-b border-ink-800 hover:bg-ink-800">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-ink-100">{session.candidate_name}</div>
                            <div className="text-xs text-ink-400">{session.candidate_id}</div>
                          </div>
                          {session.interview_status === 'interrupted' && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                              Interrupted
                            </span>
                          )}
                          {session.interview_status === 'abandoned' && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                              Abandoned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-300">{session.position}</div>
                        <div className="text-xs text-ink-400">{session.department}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-300">
                        {session.interview_date ? new Date(session.interview_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {session.has_report ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(session.report_score)}`}>
                              {session.report_score}/100
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${getRecColor(session.report_recommendation)}`}>
                              {session.report_recommendation}
                            </span>
                          </div>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium text-ink-400 bg-ink-800">
                            Not Generated
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {session.has_report ? (
                            <>
                              <button
                                onClick={() => navigate(`/report/${session.session_id}`)}
                                className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 flex items-center gap-1"
                              >
                                <Eye className="w-3 h-3" />
                                View
                              </button>
                              <button
                                onClick={() => handleDownloadPdf(session.session_id)}
                                className="px-3 py-1.5 text-xs font-medium text-ink-300 bg-ink-800 rounded-lg hover:bg-ink-800 flex items-center gap-1"
                              >
                                <Download className="w-3 h-3" />
                                PDF
                              </button>
                              <button
                                onClick={() => handleGenerateReport(session.session_id)}
                                disabled={generating === session.session_id}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 ${
                                  generating === session.session_id
                                    ? 'bg-blue-300 text-white cursor-not-allowed'
                                    : 'bg-brand-500 text-white hover:bg-brand-400'
                                }`}
                                title="Regenerate report with latest analysis"
                              >
                                {generating === session.session_id ? (
                                  <Loader className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3" />
                                )}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleGenerateReport(session.session_id)}
                              disabled={generating === session.session_id}
                              className={`px-4 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 ${
                                generating === session.session_id
                                  ? 'bg-purple-300 text-white cursor-not-allowed'
                                  : 'bg-purple-600 text-white hover:bg-purple-700'
                              }`}
                            >
                              {generating === session.session_id ? (
                                <>
                                  <Loader className="w-3 h-3 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <FileText className="w-3 h-3" />
                                  Generate Report
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {generating && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader className="w-5 h-5 animate-spin text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-purple-800">Generating 360-degree report...</p>
                  <p className="text-xs text-purple-600">
                    Analyzing interview responses and generating comprehensive assessment. This may take 30-60 seconds.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeSubTab === 'history' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-ink-100">Generated Reports</h3>
            <button
              onClick={fetchReports}
              className="flex items-center gap-2 px-3 py-2 text-sm text-ink-300 border border-ink-700 rounded-lg hover:bg-ink-800"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-ink-400">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No reports generated yet.</p>
              <p className="text-xs mt-1">Go to "Generate Reports" tab to create one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ink-800 border-b border-ink-700">
                    <th className="px-4 py-3 text-left font-medium text-ink-300">Candidate</th>
                    <th className="px-4 py-3 text-left font-medium text-ink-300">Position</th>
                    <th className="px-4 py-3 text-center font-medium text-ink-300">Score</th>
                    <th className="px-4 py-3 text-center font-medium text-ink-300">Recommendation</th>
                    <th className="px-4 py-3 text-left font-medium text-ink-300">Generated</th>
                    <th className="px-4 py-3 text-center font-medium text-ink-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.session_id} className="border-b border-ink-800 hover:bg-ink-800">
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink-100">
                          {report.metadata?.candidate_name || 'Unknown'}
                        </div>
                        <div className="text-xs text-ink-400">{report.candidate_id}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-300">
                        {report.metadata?.position || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(report.overall_score)}`}>
                          {report.overall_score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRecColor(report.recommendation)}`}>
                          {report.recommendation}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-300 text-xs">
                        {report.generated_at ? new Date(report.generated_at).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/report/${report.session_id}`)}
                            className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(report.session_id)}
                            className="px-3 py-1.5 text-xs font-medium text-ink-300 bg-ink-800 rounded-lg hover:bg-ink-800 flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            PDF
                          </button>
                          <button
                            onClick={() => handleGenerateReport(report.session_id)}
                            disabled={generating === report.session_id}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 ${
                              generating === report.session_id
                                ? 'bg-blue-300 text-white cursor-not-allowed'
                                : 'bg-brand-500 text-white hover:bg-brand-400'
                            }`}
                            title="Regenerate report"
                          >
                            {generating === report.session_id ? (
                              <Loader className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reports;
